import * as ExcelJS from "exceljs";

import { FormatDate } from "../../api-client-nswag/taxportal-client";
import { Entity, UserPerSite } from "../../api-client-nswag/custompermission-client";
import { convertFormatDate, convertUTCTimeToLocal, formatUsersCount } from "../../util/Utils";

type PublicationLifecycleStatus =
  | "Not Restricted"
  | "Modified"
  | "Saved"
  | "Scheduled"
  | "Processing"
  | "Complete";

const normalizeStatusKey = (raw?: string | null) => (raw ?? "").trim().toUpperCase();

const areStringArraysEqual = (a?: string[], b?: string[]) => {
  const aa = Array.isArray(a) ? a : [];
  const bb = Array.isArray(b) ? b : [];
  if (aa.length !== bb.length) return false;
  const as = [...aa].map(String).sort();
  const bs = [...bb].map(String).sort();
  for (let i = 0; i < as.length; i++) {
    if (as[i] !== bs[i]) return false;
  }
  return true;
};

const buildFolderUsersMap = (tree: any[]) => {
  const map = new Map<number, string[]>();
  const stack = Array.isArray(tree) ? [...tree] : [];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    const folderId = Number(node.folderId);
    if (Number.isFinite(folderId)) {
      map.set(folderId, Array.isArray(node.users) ? node.users.map(String) : []);
    }
    if (Array.isArray(node.children) && node.children.length) {
      stack.push(...node.children);
    }
  }
  return map;
};

const isEntityDirty = (entity: Entity, snapshotEntities: Entity[] = []): boolean => {
  const snap = (snapshotEntities ?? []).find((e: Entity) => e?.entityId === entity.entityId);
  if (!snap) return false;

  if (!!entity.isRestricted !== !!snap.isRestricted) return true;
  if (!areStringArraysEqual(entity.userList, snap.userList)) return true;

  const currTree = Array.isArray(entity.folderTree) ? entity.folderTree : [];
  const snapTree = Array.isArray(snap.folderTree) ? snap.folderTree : [];
  const currMap = buildFolderUsersMap(currTree);
  const snapMap = buildFolderUsersMap(snapTree);

  if (currMap.size !== snapMap.size) return true;

  let dirty = false;
  currMap.forEach((users, folderId) => {
    if (dirty) return;
    const orig = snapMap.get(folderId) ?? [];
    if (!areStringArraysEqual(users, orig)) dirty = true;
  });

  return dirty;
};

const resolveLifecycleStatus = (entity: Entity, snapshotEntities: Entity[] = []): PublicationLifecycleStatus => {
  if (isEntityDirty(entity, snapshotEntities)) return "Modified";

  const key = normalizeStatusKey(entity.publishStatus);
  if (key === "IN PROGRESS" || key === "PROCESSING") return "Processing";

  const hasPendingChanges = key === "CHANGED" || entity.isChanged === true;

  if (!hasPendingChanges && entity.publishedDate) return "Complete";

  if (key === "COMPLETED") return "Complete";
  if (key === "SCHEDULED") {
    if (hasPendingChanges) return "Scheduled";
    return entity.isRestricted ? "Saved" : "Not Restricted";
  }

  if (hasPendingChanges) return "Saved";
  if (!entity.isRestricted) return "Not Restricted";
  return "Saved";
};

export const exportManageCustomPermissionsToExcel = async (
  entities: Entity[] = [],
  users: UserPerSite[] = [],
  siteId?: string | number,
  snapshotEntities: Entity[] = []
): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Custom Permissions");

  const userIdToEmailMap = new Map(
    (users ?? []).map((siteUser: UserPerSite) => [
      String(siteUser.userId),
      siteUser.userEmail || String(siteUser.userId),
    ])
  );

  const getUsersCellValue = (entity: Entity): string => {
    if (!entity?.isRestricted) {
      const allEmails = (users ?? [])
        .map((siteUser: UserPerSite) => siteUser.userEmail)
        .filter((email: string | undefined) => Boolean(email));
      return allEmails.length ? allEmails.join(", ") : "All";
    }

    const entityUserIds = (entity?.userList ?? []).map((id: string) => String(id));
    const filteredIds = entityUserIds.filter((id: string) => userIdToEmailMap.has(id));
    if (!filteredIds.length) return formatUsersCount(0);

    const entityUserEmails = filteredIds.map((id: string) => userIdToEmailMap.get(id) || id);
    return entityUserEmails.join(", ");
  };

  const headers = [
    "Entity ID",
    "Entity Name",
    "Users",
    "Restricted",
    "Status",
    "Scheduled",
    "Published",
    "Date Modified",
    "Last Modified by",
  ];

  const headerRow = worksheet.addRow(headers);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
  };

  entities.forEach((entity: Entity) => {
    const status = resolveLifecycleStatus(entity, snapshotEntities);
    const scheduled =
      status === "Scheduled" && entity.scheduledPublishDate
        ? convertUTCTimeToLocal(entity.scheduledPublishDate, FormatDate.DD)
        : "";
    const published =
      status === "Complete" && entity.publishedDate
        ? convertUTCTimeToLocal(entity.publishedDate, FormatDate.DD)
        : "";

    const row = [
      entity.entityId || "",
      entity.entityName || "",
      getUsersCellValue(entity),
      entity.isRestricted ? "Yes" : "No",
      status,
      scheduled,
      published,
      entity.dateModified ? convertFormatDate(entity.dateModified, FormatDate.DD) : "",
      entity.lastModifiedBy || "",
    ];
    worksheet.addRow(row);
  });

  worksheet.columns = [
    { width: 12 },
    { width: 28 },
    { width: 60 },
    { width: 12 },
    { width: 14 },
    { width: 14 },
    { width: 14 },
    { width: 16 },
    { width: 22 },
  ];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  const siteIdSuffix =
    siteId !== undefined && siteId !== null && String(siteId).trim() !== ""
      ? String(siteId).trim()
      : "siteid";
  a.href = url;
  a.download = `Custom_Permissions_Export_${siteIdSuffix}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
